// === EmailStatus.jsx (responsive header redesign + trail + branded draft preview + proposals + voice notes + S3 pre-checks) ===
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import HeaderMainPage from '../../header/HeaderMainPage';
import Swal from 'sweetalert2';
import { fetchCampaignCompanies } from '../managingPage/CompanyDataUtils';
import { FaQuestionCircle, FaEnvelope, FaLinkedin, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { FiRefreshCw, FiSettings, FiInbox, FiZap, FiEye } from 'react-icons/fi';
import { FiMail, FiPaperclip, FiRepeat, FiFlag, FiMic, FiFileText, FiMessageSquare, FiCheck } from 'react-icons/fi';
import ExplainDISC from '../utils/ExplainDISC';
import ExplainOCEAN from '../utils/ExplainOCEAN';
import { PromptBox } from './PromptBox';
import ReactQuill from "react-quill";
import { IoArrowBack } from "react-icons/io5";
import 'quill/dist/quill.snow.css';
import CampaignSummary from "../managingPage/campaignSummary";

// ---------- Styles ----------
const BTN_BASE ="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_PRIMARY = `${BTN_BASE} bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600`;
const BTN_NEUTRAL = `${BTN_BASE} bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 focus-visible:ring-gray-900`;
const BTN_DANGER = `${BTN_BASE} bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600`;
const BTN_SUCCESS = `${BTN_BASE} bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-600`;
const BTN_SMALL = "rounded-md px-3 py-1 text-xs font-semibold";

// header tool buttons (new look)
const TOOL_BTN ="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition";
const ICON_GHOST ="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 shadow-sm transition";
const MENU_PANEL ="absolute top-full left-0 mt-1 w-max min-w-[220px] rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 z-50 p-1";
const MENU_ITEM ="block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100";

// ---------- Status badge ----------
const getStatusStyle = (status) => {
  switch (status) {
    case 1: return { style: 'bg-gray-100 text-gray-700', text: 'Not Started' };
    case 2: return { style: 'bg-blue-100 text-blue-700', text: 'Waiting to Review' };
    case 3: return { style: 'bg-yellow-100 text-yellow-700', text: 'Reviewed' };
    case 4: return { style: 'bg-green-100 text-green-700', text: 'Sent' };
    case 5: return { style: 'bg-amber-100 text-amber-700', text: 'Opened' };
    case 6: return { style: 'bg-purple-100 text-purple-700', text: 'Replied' };

    // ✅ DB says 7 = responded
    case 7: return { style: 'bg-indigo-100 text-indigo-700', text: 'Responded' };

    // ✅ DB says 8 = generating
    case 8: return { style: 'bg-gray-100 text-gray-700', text: 'Generating' };

    // ✅ DB says 9 = generate failed
    case 9: return { style: 'bg-red-100 text-red-700', text: 'Generate Failed' };

    // ✅ DB says 10 = sending
    case 10: return { style: 'bg-gray-100 text-gray-700', text: 'Sending' };

    // ✅ DB says 11 = send failed
    case 11: return { style: 'bg-red-100 text-red-700', text: 'Send Failed' };

    // ✅ DB says 12 = clicked
    case 12: return { style: 'bg-sky-100 text-sky-700', text: 'Clicked' };

    default: return { style: 'bg-transparent text-gray-800', text: 'Unknown' };
  }
};

const getLinkedinStyleStatus = (status) => {
  switch (status) {
    case 1: return { style: 'bg-amber-100 text-amber-700', text: 'In Progress' };
    case 2: return { style: 'bg-blue-100 text-blue-700', text: 'Sent' };
    case 3: return { style: 'bg-yellow-100 text-yellow-700', text: 'Opened' };
    case 4: return { style: 'bg-green-100 text-green-700', text: 'Replied' };
    case 5: return { style: 'bg-amber-100 text-amber-700', text: 'Responded' };
    case 6: return { style: 'bg-purple-100 text-purple-700', text: 'Sending' };
    case 7: return { style: 'bg-red-100 text-red-700', text: 'Send Failed' };
    default: return { style: 'bg-transparent text-gray-800', text: 'Unknown' };
  }
};

const modules = {
  toolbar: [
    [{ font: [] }, { size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};

const MODAL_Z = "z-[9999]";

/* ==== Static sequence templates for all approaches ==== */
const SEQUENCES = {
  conservative: {
    EMAIL: [
      { key: 0, label: "Initial mail", Icon: FiMail },
      { key: 1, label: "First follow-up", Icon: FiRepeat },
      { key: 2, label: "Second follow-up", Icon: FiRepeat },
      { key: 3, label: "Final follow-up", Icon: FiFlag },
    ],
    LINKEDIN: [
      { key: 0, label: "Initial message", Icon: FaLinkedin },
      { key: 1, label: "First follow-up", Icon: FiRepeat },
      { key: 2, label: "Second follow-up", Icon: FiRepeat },
      { key: 3, label: "Last follow-up", Icon: FiFlag },
    ],
  },
  balanced: {
    EMAIL: [
      { key: 0, label: "Initial mail", Icon: FiMail },
      { key: 1, label: "First follow-up", Icon: FiRepeat },
      { key: 2, label: "Second follow-up + PDF proposal", Icon: FiFileText },
      { key: 3, label: "Final follow-up", Icon: FiFlag },
    ],
    LINKEDIN: [
      { key: 0, label: "Initial message", Icon: FaLinkedin },
      { key: 1, label: "First follow-up", Icon: FiRepeat },
      { key: 2, label: "Voice note", Icon: FiMic },
      { key: 3, label: "PDF proposal", Icon: FiFileText },
    ],
  },
  aggressive: {
    EMAIL: [
      { key: 0, label: "Initial mail + PDF proposal", Icon: FiFileText },
      { key: 1, label: "First follow-up", Icon: FiRepeat },
      { key: 2, label: "Second follow-up", Icon: FiRepeat },
      { key: 3, label: "Final follow-up", Icon: FiFlag },
    ],
    LINKEDIN: [
      { key: 0, label: "Initial message", Icon: FaLinkedin },
      { key: 1, label: "PDF proposal", Icon: FiFileText },
      { key: 2, label: "Voice note", Icon: FiMic },
      { key: 3, label: "Final follow-up message", Icon: FiFlag },
    ],
  },
};

/* ==== Helpers for progress steps & labels ==== */
const isNil = (n) => n === null || n === undefined;
const clampStep = (n) => (typeof n === "number" ? Math.min(Math.max(n, 0), 3) : 0);
const labelFor = (steps, n) =>
  isNil(n) ? "Not started" : steps[clampStep(n)]?.label || "Not started";
const iconFor = (steps, n) =>
  isNil(n) ? FiMessageSquare : (steps[clampStep(n)]?.Icon || FiMessageSquare);

// small “chip” for the table cell
const Chip = ({ icon: Icon, text, active }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
    border ${active
        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
        : 'bg-gray-50 border-gray-200 text-gray-600'
      }`}
  >
    <Icon className={`text-sm ${active ? 'animate-pulse' : ''}`} />
    <span className="truncate max-w-[140px]">{text}</span>
  </span>
);

// node + connector used inside the trail
const StepNode = ({ state, Icon }) => {
  const base = "relative flex items-center justify-center w-10 h-10 rounded-full shadow-sm";
  if (state === "done") {
    return (
      <div className={`${base} bg-indigo-600 text-white`}>
        <FiCheck className="text-lg" />
      </div>
    );
  }
  if (state === "current") {
    return (
      <div className={`${base} bg-white border-2 border-indigo-400 text-indigo-600`}>
        {/* ping halo */}
        <span className="absolute inline-flex h-10 w-10 rounded-full animate-ping border-2 border-indigo-300 opacity-70"></span>
        <Icon className="text-lg animate-bounce" />
      </div>
    );
  }
  return (
    <div className={`${base} bg-white border-2 border-gray-300 text-gray-400`}>
      <Icon className="text-lg" />
    </div>
  );
};

const Trail = ({ steps, current }) => (
  <div className="px-1 py-3">
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const state = i < current ? "done" : i === current ? "current" : "todo";
        return (
          <React.Fragment key={s.key}>
            <StepNode state={state} Icon={s.Icon} />
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-1 rounded-full ${i < current ? 'bg-indigo-500' : i === current ? 'bg-indigo-300' : 'bg-gray-200'
                  }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
    <div className="mt-2 flex justify-between text-[10px] sm:text-xs text-gray-600">
      {steps.map((s) => (
        <div key={s.key} className="w-20 sm:w-28 text-center px-1 truncate">
          {s.label}
        </div>
      ))}
    </div>
  </div>
);

/** -------- Branded Email Template Preview ---------- */
const EmailTemplatePreview = ({ subject, htmlBody, calendlyLink }) => {
  const bodyHtml = htmlBody?.trim() ? htmlBody : "<p></p>";
  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-3xl bg-gray-50 rounded-2xl overflow-hidden border border-gray-200">
        <div className="bg-indigo-600 text-white px-6 py-6">
          <h2 className="text-xl md:text-2xl font-bold leading-snug">
            {subject || '—'}
          </h2>
        </div>
        <div className="bg-white px-6 py-6 md:px-10 md:py-8">
          <div
            className="prose max-w-none prose-p:my-3 prose-p:text-gray-800 prose-ul:my-3 prose-ol:my-3"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          {calendlyLink ? (
            <div className="mt-6">
              <a
                href={calendlyLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Book a Meeting →
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/* ====================== PROPOSAL (PDF) HELPERS ====================== */
const PROPOSAL_BUCKET_BASE = "https://autopilotproposals.s3.ap-south-1.amazonaws.com";
/* ====================== VOICE NOTE HELPERS ====================== */
const VOICE_BUCKET_BASE = "https://autopilotvoicenotes.s3.ap-south-1.amazonaws.com"; // change if your voice bucket differs

// decode base64url safely
const _b64UrlDecode = (input) => {
  try {
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    const padded = base64 + (pad ? '='.repeat(4 - pad) : '');
    return atob(padded);
  } catch {
    return null;
  }
};

// read user_id from JWT in localStorage("access_token")
const getUserIdFromToken = () => {
  try {
    const auth = (localStorage.getItem('access_token') || '').trim();
    if (!auth) return localStorage.getItem('user_id') || null;
    const parts = auth.split(/\s+/);
    const token = parts.length > 1 ? parts[1] : parts[0]; // handle "Bearer <jwt>" or plain jwt
    const segs = token.split('.');
    if (segs.length !== 3) return localStorage.getItem('user_id') || null;
    const payloadStr = _b64UrlDecode(segs[1]);
    if (!payloadStr) return localStorage.getItem('user_id') || null;
    const payload = JSON.parse(payloadStr);
    return payload.user_id || payload.sub || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};

// for S3 key: spaces must be '+'
const encodeRepForS3 = (name) => (name || '').trim().replace(/\s+/g, '+');

// --- PDF proposal URL (two variants) ---
const proposalS3UrlPlus = (campaignId, repName, userId) =>
  `${PROPOSAL_BUCKET_BASE}/${encodeURIComponent(campaignId)}__${encodeRepForS3(repName)}__${encodeURIComponent(userId)}.pdf`;
const proposalS3UrlPct = (campaignId, repName, userId) =>
  `${PROPOSAL_BUCKET_BASE}/${encodeURIComponent(campaignId)}__${encodeURIComponent((repName || '').trim())}__${encodeURIComponent(userId)}.pdf`;

// --- Voice note URL (two variants) ---
const voiceS3UrlPlus = (campaignId, repName, userId) =>
  `${VOICE_BUCKET_BASE}/${encodeURIComponent(campaignId)}__${encodeRepForS3(repName)}__${encodeURIComponent(userId)}_voice.m4a`;
const voiceS3UrlPct = (campaignId, repName, userId) =>
  `${VOICE_BUCKET_BASE}/${encodeURIComponent(campaignId)}__${encodeURIComponent((repName || '').trim())}__${encodeURIComponent(userId)}_voice.m4a`;

/**
 * Probe S3 with a lightweight GET. If CORS blocks, return null (unknown),
 * not false, so we don't disable buttons incorrectly.
 * Returns: true | false | null
 */
const s3ObjectExists = async (url) => {
  const classify = (status) => {
    if (status === 200 || status === 206) return true;   // OK / Partial Content
    if (status === 404) return false;                     // Not Found
    if (status === 301 || status === 302 || status === 307 || status === 308){
      // redirect:'follow' should resolve this; treat anything left over as unknown
      return null;
    }
    if (status === 401 || status === 403) return null;    // private/no CORS -> unknown
    return null;                                          // anything else => unknown
  };

  try {
    // Try a HEAD first (fastest)
    const head = await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      redirect: 'follow',
      credentials: 'omit',
    });
    const headResult = classify(head.status);
    if (headResult !== null) return headResult;
  } catch {
    // ignore and try GET below
  }

  try {
    // Fallback: tiny ranged GET (also yields 206 when present)
    const getRes = await fetch(url,{
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      cache: 'no-store',
      redirect: 'follow',
      credentials: 'omit',
    });
    return classify(getRes.status);
  } catch {
    return null; // network/CORS => unknown
  }
};

const EmailStatus = () => {
  const navigate = useNavigate();
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;
  const campaign_id = localStorage.getItem("campaign_id");
  const senderConfig = { name: localStorage.getItem("nickname") };

  // ---- Progress trails (0..3) ----
  const [approach, setApproach] = useState("conservative");

  // Fetch approach from backend and normalize: "conservative" | "balanced" | "aggressive"
  useEffect(() => {
    const fetchApproach = async () => {
      try {
        const response = await fetch(
          `${roboman_api}/campaign_fetch_approach/${campaign_id}`,
          {
            headers: {
              accept: "application/json",
              Authorization: localStorage.getItem("access_token"),
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          // Handle multiple possible response shapes safely
          let value;
          if (typeof data === "string") {
            value = data;
          } else if (Array.isArray(data)) {
            value = data[0];
          } else if (data && typeof data === "object") {
            value = data.approach || data.name;
          }
          setApproach(value?.toLowerCase?.() || "conservative");
        }
      } catch (error) {
        console.error("Failed to fetch campaign approach:", error);
      }
    };
    if (campaign_id && roboman_api) {
      fetchApproach();
    }
  }, [campaign_id, roboman_api]);

  // Pick the correct steps dynamically for this campaign
  const EMAIL_STEPS = SEQUENCES[approach]?.EMAIL || SEQUENCES.conservative.EMAIL;
  const LI_STEPS = SEQUENCES[approach]?.LINKEDIN || SEQUENCES.conservative.LINKEDIN;

  // ---------------- Core state ----------------
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [showPromptBox, setShowPromptBox] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptButtonPosition, setPromptButtonPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const promptButtonRef = useRef(null);
  const promptBoxRef = useRef(null);
  const [campaignState, setCampaignState] = useState(null);
  const isLnkActive = localStorage.getItem('lnk_act') === 'true';
  let Campaign_Name = localStorage.getItem("campaign_name");
  const authToken = localStorage.getItem("access_token");

  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [showProspectMenu, setShowProspectMenu] = useState(false);
  const ActionMenuRef = useRef(null);
  const ConfigMenuRef = useRef(null);
  const ProspectViewMenuRef = useRef(null);

  const [localCompaniesData, setLocalCompaniesData] = useState([]);
  const hasRunGenerateSelected = useRef(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [numProspecttoView, setNumProspecttoView] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [autopilotStatus, setAutoPilotStatus] = useState(null);

  const [isSendingSingle, setIsSendingSingle] = useState(false);

  // NEW: per-row S3 asset availability (proposal / voice)
  // value shape: { [company_id]: { proposal: true|false|null, proposalUrl?:string, voice: true|false|null, voiceUrl?:string, voiceAltUrl?:string } }
  const [assetAvailability, setAssetAvailability] = useState({});

  // ---------- Modal flags ----------
  const [showPopup, setShowPopup] = useState(false);
  const [isPopUpAddContact, setPopUpAddContact] = useState(false);
  const [isPromptBoxOpen, setPromptBoxOpen] = useState(false);
  const [showAutoPilotPanel, setShowAutoPilotPanel] = useState(false);
  const [showSetupMeetingPopup, setShowSetupMeetingPopup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOCEANModalOpen, setIsOCEANModalOpen] = useState(false);

  // NEW: progress modal state
  const [progressModal, setProgressModal] = useState({
    open: false,
    companyName: "",
    emailStep: 0,
    liStep: 0,
  });

  // NEW: Voice note modal state
  const [voiceModal, setVoiceModal] = useState({
    open: false,
    repName: "",
    url: "",
    altUrl: "",
    triedAlt: false,
  });

  const [showAddProspectsPopup, setShowAddProspectsPopup] = useState(false);
  const [prospectsToAdd, setProspectsToAdd] = useState(10); // default number
  const [isLeadsPolling, setIsLeadsPolling] = useState(false);

  const openProgress = (row) => {
    setProgressModal({
      open: true,
      companyName: capitalizeWords(row.company_name || ""),
      emailStep: isNil(row?.outreach_progress) ? 0 : clampStep(row?.outreach_progress),
      liStep: isNil(row?.linkedin_outreach_progress) ? 0 : clampStep(row?.linkedin_outreach_progress),
    });
  };
  const closeProgress = () => setProgressModal((p) => ({ ...p, open: false }));

  // ---------- Autopilot status ----------
  const handleGetAutoPilotStatus = async () => {
    try {
      const response = await fetch(`${roboman_api}/campaign/${campaign_id}/outreach/get-auto-status`, {
        method: "GET",
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.auto_outreach;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      const s = await handleGetAutoPilotStatus();
      setAutoPilotStatus(s);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ActionMenuRef.current && !ActionMenuRef.current.contains(event.target)) setShowActionMenu(false);
      if (ConfigMenuRef.current && !ConfigMenuRef.current.contains(event.target)) setShowConfigMenu(false);
      if (ProspectViewMenuRef.current && !ProspectViewMenuRef.current.contains(event.target)) setShowProspectMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---------- counts / pagination ----------
  const calculateTotalPages = (totalItems, itemsPerPage) => Math.ceil(totalItems / itemsPerPage);

  const fetchCompanyCount = async (campaignId) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/${campaignId}/companies/count`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token')
        }
      });
      if (!response.ok) {
        Swal.fire({ icon: 'error', title: 'Fetch Failed', text: `Error: ${response.status} - ${response.statusText}` });
        return null;
      }
      const data = await response.json();
      return data.company_count;
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Fetch Failed', text: error.message });
      return null;
    }
  };

  const [totalPages, setTotalPages] = useState(null);
  const [numOfProspect, setNumOfProspect] = useState(0);

  useEffect(() => {
    (async () => {
      const count = await fetchCompanyCount(campaign_id);
      if (count !== null) setNumOfProspect(count);
    })();
  }, [campaign_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTotalPages(calculateTotalPages(numOfProspect, numProspecttoView));
  }, [numOfProspect, numProspecttoView]);

  useEffect(() => {
    (async (page, size) => {
      const data = await fetchCampaignCompanies(campaign_id, page, size, authToken);
      setLocalCompaniesData(data === null ? [] : data);
      setIsDataReady(true);
    })(currentPage, numProspecttoView);
  }, [currentPage, numProspecttoView, campaign_id, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWithTimeout = (url, options = {}, timeout = 60000) => {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), timeout)),
    ]);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const pollLeadsMarker = async (campaignId, { intervalMs = 5000, maxAttempts = 240 } = {}) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetchWithTimeout(`${roboman_api}/campaign/leads-poll/${campaignId}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("access_token"),
          },
        }, 15000); // 15s timeout per call

        if (res.ok) {
          const result = await res.json();
          const running = !!result?.leads_poll_marker;
          if (!running) return { done: true };
        }
      } catch (err) {
        console.warn("Poll error:", err.message);
        // keep retrying unless total timeout reached
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return { done: false };
  };

  const handleAddProspectsPopup = () => {
    setShowConfigMenu(false);
    setShowAddProspectsPopup(true);
  };

  const handleCloseAddProspects = () => {
    setShowAddProspectsPopup(false);
    setProspectsToAdd(10);
  };

  const handleAddProspects = async () => {
    const campaignId = localStorage.getItem("campaign_id");
    if (!campaignId || !prospectsToAdd || prospectsToAdd <= 0) {
      Swal.fire({ icon: "warning", title: "Invalid Input", text: "Please enter a valid number of prospects." });
      return;
    }

    if (isLeadsPolling) return; // prevent double clicks

    try {
      setIsLeadsPolling(true);

      // Step A: Queue the job
      Swal.fire({
        title: "Queuing job...",
        html: `Requesting <b>${prospectsToAdd}</b> additional prospects.`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await fetch(`${roboman_api}/campaign/add-prospects`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: localStorage.getItem("access_token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          desired_count: prospectsToAdd,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        Swal.close();
        Swal.fire({ icon: "error", title: "Failed", text: data.detail || "Could not add new prospects." });
        return;
      }

      // close your modal now (user confirmed already)
      handleCloseAddProspects();

      // Step B: Show loader while worker runs, and poll the marker
      Swal.fire({
        title: "Fetching Prospects...",
        html: "This may take a minute or two.<br><b>Please be patient.</b>",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const pollResult = await pollLeadsMarker(campaignId, { intervalMs: 5000, maxAttempts: 60 });

      Swal.close();

      if (pollResult.done) {
        Swal.fire({
          icon: "success",
          title: "Prospects Fetched 🎯",
          text: "New leads were successfully added to your campaign.",
        }).then(async () => {
          // refresh table + counts
          await updateData(currentPage, numProspecttoView);
          const count = await fetchCompanyCount(campaignId);
          if (count !== null) setNumOfProspect(count);
        });
      } else {
        Swal.fire({
          icon: "info",
          title: "Still Processing",
          text: "Fetching may still be in progress. Please refresh after some time.",
        });
      }
    } catch (error) {
      Swal.close();
      Swal.fire({ icon: "error", title: "Network Error", text: error?.message || "Something went wrong." });
    } finally {
      setIsLeadsPolling(false);
    }
  };


  // ---------- S3 pre-checks for proposals & voice notes ----------
  const checkAssetsForRows = async (rows) => {
    const userId = getUserIdFromToken();
    if (!userId) return;

    const pairs = await Promise.all(
      rows.map(async (row) => {
        const repName = row?.rep_name || '';
        const companyId = row.company_id;

        // Proposals: try PLUS then PCT
        const p1 = proposalS3UrlPlus(campaign_id, repName, userId);
        const p1ok = await s3ObjectExists(p1);
        let proposal = null; // null=unknown
        let proposalUrl = null;

        if (p1ok === true) { proposal = true; proposalUrl = p1; }
        else {
          const p2 = proposalS3UrlPct(campaign_id, repName, userId);
          const p2ok = await s3ObjectExists(p2);
          if (p2ok === true) { proposal = true; proposalUrl = p2; }
          else if (p1ok === false && p2ok === false) { proposal = false; } // confirmed missing
          // else stay null (unknown due to CORS)
        }

        // Voice: try PLUS then PCT; keep alt as fallback for player
        const v1 = voiceS3UrlPlus(campaign_id, repName, userId);
        const v1ok = await s3ObjectExists(v1);
        let voice = null;
        let voiceUrl = null;
        let voiceAltUrl = null;

        if (v1ok === true) { voice = true; voiceUrl = v1; }
        else {
          const v2 = voiceS3UrlPct(campaign_id, repName, userId);
          const v2ok = await s3ObjectExists(v2);
          if (v2ok === true) { voice = true; voiceUrl = v2; }
          else if (v1ok === false && v2ok === false) { voice = false; }
          voiceAltUrl = v2; // keep as alt regardless
        }

        return [companyId, { proposal, proposalUrl, voice, voiceUrl, voiceAltUrl }];
      })
    );

    const map = {};
    pairs.forEach(([id, val]) => { map[id] = val; });
    setAssetAvailability((prev) => ({ ...prev, ...map }));
  };

  // Run checks whenever the visible rows change
  useEffect(() => {
    if (localCompaniesData && localCompaniesData.length) {
      checkAssetsForRows(localCompaniesData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localCompaniesData, campaign_id]);

  // ---------- modal stacking / scroll lock ----------
  const anyModalOpen =
    showPopup ||
    isPopUpAddContact ||
    isPromptBoxOpen ||
    !!selectedCampaign ||
    showAutoPilotPanel ||
    showSetupMeetingPopup ||
    isModalOpen ||
    isOCEANModalOpen ||
    progressModal.open ||
    voiceModal.open; // include voice modal

  useEffect(() => {
    const original = document.body.style.overflow;
    if (anyModalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = original || "";
    return () => { document.body.style.overflow = original || ""; };
  }, [anyModalOpen]);

  // ---------- selection ----------
  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    const newSelectedRows = {};
    localCompaniesData.forEach(row => { newSelectedRows[row.company_id] = isChecked; });
    setSelectedRows(newSelectedRows);
  };

  const handleRowSelect = (id) => {
    const newSelectedRows = { ...selectedRows, [id]: !selectedRows[id] };
    setSelectedRows(newSelectedRows);
    setSelectAll(
      Object.values(newSelectedRows).every(Boolean) &&
      Object.keys(newSelectedRows).length === localCompaniesData.length
    );
  };

  // ---------- core actions ----------
  const handleAutoSend = async () => {
    const updatedData = await Promise.all(
      localCompaniesData.map(async (row) => {
        if (row.content === null) return { ...row, email_confirmation_status_id: 'Failed to Send' };
        try {
          await sendEmail(row);
          return { ...row, email_confirmation_status_id: 2 };
        } catch {
          return { ...row, email_confirmation_status_id: 'Failed to Send' };
        }
      })
    );
    setLocalCompaniesData(updatedData);
  };

  const handleMailboxClick = () => navigate("/unibox");
  const handleBack = () => { localStorage.removeItem("campaign_id"); navigate('/my-campaign'); };

  const generateEmail = async (company_id_list, prompt_name) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/emails/generate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id_list,
          campaign_id: localStorage.getItem('campaign_id'),
          prompt_name,
        }),
      });
      if (!response.ok) return false;
      return true;
    } catch {
      return false;
    }
  };

  const generateLinkedInMessage = async (reply_name, linkedInlink) => {
    const productName = localStorage.getItem('core_service');
    const keyBenefits = localStorage.getItem('key_benefits');
    const problemSolved = localStorage.getItem('problem_solved');
    const sendername = localStorage.getItem('nickname');
    const linkedin_generate_prompt = `
    Write a short linkedin message using the information below
    Name of sender or email writer: ${sendername}
    Name of receiver: ${reply_name}
    Product name: ${productName}
    Key Benefits: ${keyBenefits}
    Problem Solved: ${problemSolved}`;
    try {
      const response = await fetch(`${roboman_api}/campaign/email/generation`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ input_str: linkedin_generate_prompt.trim(), rep_linkedin: linkedInlink }),
      });
      if (!response.ok) return false;
      const responseData = await response.text();
      const responseText = JSON.parse(responseData);
      const responseText1 = responseText.generated_email;
      try {
        JSON.parse(responseText1);
        localStorage.setItem('parsedData', responseText1);
        return true;
      } catch {
        return false;
      }
    } catch {
      return false;
    }
  };

  const handleGenerate = async (company_id) => {
    Swal.fire({
      title: 'Generating Email',
      text: 'Please wait while the email content is being generated.',
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    setLocalCompaniesData((prev) =>
      prev.map((row) =>
        row.company_id === company_id ? { ...row, email_confirmation_status_id: 7, content: "Generating..." } : row
      )
    );

    let prompt_type = "email_generation_v1";
    if (selectedPrompt != null) prompt_type = selectedPrompt.prompt_name;

    const ok = await generateEmail([company_id], prompt_type);
    await new Promise((r) => setTimeout(r, 8000));
    Swal.close();

    Swal.fire({
      title: ok ? 'Success!' : 'Error!',
      text: ok ? 'Email content has been generated.' : 'Failed to generate email content. Please try again.',
      icon: ok ? 'success' : 'error',
      confirmButtonText: 'OK'
    }).then(() => updateData(currentPage, numProspecttoView));
  };

  const sendEmail = async (comp_id_list) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/emails/send`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_id_list: comp_id_list })
      });
      if (!response.ok) {
        const errorData = await response.text();
        console.error('API Error:', errorData);
      }
    } catch (error) {
      console.log('Email sending error:', error);
    }
  };

  const handleSend = async (company_id) => {
    const rowData = localCompaniesData.find((row) => row.company_id === company_id);
    if (!rowData.content?.trim() || !rowData.content_subject?.trim()) {
      Swal.fire({ icon: 'error', title: 'Empty Content', text: 'Please add content and subject before sending the email' });
      return;
    }

    try {
      Swal.fire({ title: 'Sending...', text: 'Please wait while the email is being sent.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      sendEmail([company_id]);
      await updateEmailStatus(company_id, rowData.content_subject, rowData.content, 4);
    } catch {
      await updateEmailStatus(company_id, rowData.content_subject, rowData.content, 10);
    }

    await new Promise((resolve) => setTimeout(resolve, 5000));
    Swal.close();
    await updateData(currentPage, numProspecttoView);
  };

  const updateData = async (page, size) => {
    const data = await fetchCampaignCompanies(campaign_id, page, size, localStorage.getItem("access_token"));
    setLocalCompaniesData(data === null ? [] : data);
    setIsDataReady(true);
  };

  // ---------- review modal ----------
  const [editingSubject, setEditingSubject] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [emailData, setEmailData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState(true); // NEW: default to branded Preview

  const handleReview = async (company_id) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/emails/draft/${company_id}`, {
        method: "GET",
        headers: { "accept": "application/json", "Authorization": localStorage.getItem('access_token') }
      });
      if (!response.ok) {
        Swal.fire({ icon: "error", title: "Error!", text: `Failed to fetch email drafts: ${response.status} ${response.statusText}` });
        return;
      }
      const data = await response.json();
      const sorted = [...data].sort((a, b) => a.draft_type - b.draft_type);
      setEmailData(sorted);
      setCurrentIndex(0);
      setPreviewMode(true); // open in preview
      setShowPopup(true);
    } catch {
      Swal.fire({ icon: "error", title: "Network Error", text: "Something went wrong while fetching emails. Please try again later." });
    }
  };

  const handleAcceptChange = async () => {
    setShowPopup(false);
    await updateEmailStatus(campaign_id, editingId, editingSubject, editingContent, 4)
    await updateData(currentPage, numProspecttoView);
  };

  const handleCancel = async () => {
    setShowPopup(false);
    const mainMail = emailData[0];
    // await updateEmailStatus(mainMail.company_id, mainMail.subject, mainMail.body, 3)
    await updateData(currentPage, numProspecttoView);
    setEmailData([]); setCurrentIndex(0);
  };

  const handlePrev = () => setCurrentIndex((i) => (i - 1 + emailData.length) % emailData.length);
  const handleNext = () => setCurrentIndex((i) => (i + 1) % emailData.length);

  let currentEmail = emailData[currentIndex];

  const getTitle = () => {
    const titles = { 0: "View Email", 3: "View Final Follow-up Email" };
    return currentEmail ? (titles[currentEmail.draft_type] || "View Follow-up Email") : "View Email";
  };

  const handleChangeSubject = (subject) => {
    setEmailData((prev) => prev.map((email, index) => index === currentIndex ? { ...email, subject } : email));
  };
  const handleChangeBody = (body) => {
    setEmailData((prev) => prev.map((email, index) => index === currentIndex ? { ...email, body } : email));
  };

  const handleUpdateMail = async () => {
    try {
      const response = await fetch(`${roboman_api}/campaign/emails/draft/update`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: currentEmail.company_id,
          draft_id: currentEmail.draft_id,
          subject: currentEmail.subject,
          body: currentEmail.body,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        Swal.fire({ title: 'Error!', text: data.message || 'Failed to update email draft.', icon: 'error' });
        return;
      }
      Swal.fire({ title: 'Success!', text: 'Email draft updated successfully.', icon: 'success' });
    } catch {
      Swal.fire({ title: 'Error!', text: 'An unexpected error occurred. Please try again.', icon: 'error' });
    }
  };

  // ---------- add contact ----------
  const [newContact, setNewContact] = useState({
    company_name: '',
    industry: '',
    company_email: '',
    company_linkedin: '',
    rep_name: '',
    rep_email: '',
    rep_linked: '',
  });

  const handleAddContactButton = () => { setPopUpAddContact(true); setShowConfigMenu(false); };

  const handleAddContact = async () => {
    const { company_name, industry, company_email, rep_email } = newContact;
    if (!company_name || !industry || !company_email || !rep_email) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in all the required fields: Company Name, Industry, Company Email, and Reply Email.',
      });
      return;
    }
    try {
      const contactData = {
        company_name: newContact.company_name,
        industry: newContact.industry,
        company_email: newContact.company_email,
        company_linkedin: decodeURIComponent(newContact.company_linkedin),
        rep_name: newContact.rep_name,
        rep_email: newContact.rep_email,
        rep_linkedin_address: decodeURIComponent(newContact.rep_linked),
      };
      const response = await fetch(`${roboman_api}/campaign/${localStorage.getItem("campaign_id")}/companies/add`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });
      if (!response.ok) {
        Swal.fire({ icon: 'error', title: 'Error!', text: 'There was a problem adding the contact. Please try again later.' });
        return;
      }
      await response.json();
      setNewContact({ company_name: '', industry: '', company_email: '', company_linkedin: '', rep_name: '', rep_email: '', rep_linked: '' });
      Swal.fire({ icon: 'success', title: 'Success!', text: 'The contact has been successfully added.' })
        .then(() => window.location.reload());
    } catch {
      Swal.fire({ icon: 'error', title: 'Error!', text: 'There was a problem adding the contact. Please try again later.' });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const newContactWithId = { ...newContact, id: Date.now() };
    setLocalCompaniesData((prevData) => [newContactWithId, ...prevData]);
    setNewContact({ company_name: '', industry: '', company_email: '', company_linkedin: '', rep_name: '', rep_email: '', rep_linked: '' });
    setPopUpAddContact(false);
  };
  const handlePopUpClose = () => setPopUpAddContact(false);

  // ---------- batch actions ----------
  const [generatePromptAction, setGeneratePromptAction] = useState(false);

  const handleGenerateSelected = async (all = false) => {
    if (showActionMenu) toggleActionMenu();

    const selectedIds = all
      ? localCompaniesData.map((row) => row.company_id)
      : Object.keys(selectedRows).filter((company_id) => selectedRows[company_id]);

    if (selectedIds.length === 0) {
      Swal.fire({ title: 'No Emails Selected', text: 'Please select at least one row to generate an email.', icon: 'warning' });
      setSelectedRows({}); setSelectAll(false); return;
    }

    const rowsToGenerate = selectedIds.filter((company_id) => {
      const row = localCompaniesData.find((row) => row.company_id === company_id);
      return row && ![2, 4, 5, 6, 7, 9, 10].includes(row.email_confirmation_status_id);
    });

    if (rowsToGenerate.length === 0) {
      Swal.fire({ title: 'No Emails Ready', text: 'Please check again the status of the mail', icon: 'warning' });
      setSelectedRows({}); setSelectAll(false); return;
    }

    Swal.fire({ title: 'Generating Email', text: 'Please wait while the email content is being generated.', icon: 'info', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    setLocalCompaniesData((prevData) =>
      prevData.map((row) =>
        rowsToGenerate.includes(row.company_id)
          ? { ...row, email_confirmation_status_id: 7, content: "Generating..." }
          : row
      )
    );

    let prompt_type = "email_generation_v1";
    if (generatePromptAction) prompt_type = selectedPromptAction?.prompt_name || "email_generation_v1";
    const ok = await generateEmail(rowsToGenerate, prompt_type);
    await new Promise((resolve) => setTimeout(resolve, 8000 * rowsToGenerate.length));

    Swal.close();
    Swal.fire({
      title: ok ? 'Success!' : 'Error!',
      text: ok ? 'Email content has been successfully generated and updated.' : 'Failed to generate email content. Please try again.',
      icon: ok ? 'success' : 'error',
    });

    await updateData(currentPage, numProspecttoView);
    setSelectedRows({}); setSelectAll(false);
  };

  const handleSendSelected = async (all = false) => {
    if (showActionMenu) toggleActionMenu();

    const selectedIds = all
      ? localCompaniesData.map((row) => row.company_id)
      : Object.keys(selectedRows).filter((company_id) => selectedRows[company_id]);

    if (selectedIds.length === 0) return;

    const rowsToSend = selectedIds.filter((company_id) => {
      const row = localCompaniesData.find((row) => row.company_id === company_id);
      return row && ([2, 3, 4, 5, 6, 10].includes(row.email_confirmation_status_id));
    });

    if (rowsToSend.length === 0) {
      Swal.fire({ icon: "error", title: "No Prospect Ready", text: "Please check again the prospect. Mails maybe are not created or sent" });
      return;
    }

    Swal.fire({ title: "Loading...", text: "Please wait while sendind mail.", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try { await sendEmail(rowsToSend); } catch (error) { console.error("Error sending email", error); }

    Swal.close();
    await updateData(currentPage, numProspecttoView);
    setSelectedRows({}); setSelectAll(false);
  };

  const handleDeleteSelected = async () => {
    if (showActionMenu) toggleActionMenu();

    const selectedIds = Object.keys(selectedRows).filter((company_id) => selectedRows[company_id]);
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "error", title: "No Rows Selected for Deletion", text: "Please select rows to delete." });
      return;
    }

    const rowsToDelete = selectedIds.filter((company_id) => localCompaniesData.find((row) => row.company_id === company_id));
    if (rowsToDelete.length === 0) {
      Swal.fire({ icon: "error", title: "No Rows Found for Deletion", text: "There were no rows found to delete. Please select valid rows." });
      return;
    }

    const batchSize = 3;
    let successCount = 0; let failureCount = 0;

    Swal.fire({ title: "Deleting...", text: "Please wait while deleting records.", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    for (let i = 0; i < rowsToDelete.length; i += batchSize) {
      const batch = rowsToDelete.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (company_id) => {
          try {
            const response = await fetch(`${roboman_api}/campaign/companies/${company_id}/delete`, {
              method: 'DELETE',
              headers: { 'accept': 'application/json', 'Authorization': localStorage.getItem('access_token') },
            });
            if (!response.ok) throw new Error(`Failed to delete company_id: ${company_id}`);
            successCount++;
          } catch {
            failureCount++;
          }
        })
      );
      await new Promise((r) => setTimeout(r, 2000));
    }

    Swal.close();
    setSelectedRows({}); setSelectAll(false);

    if (failureCount === 0) {
      Swal.fire({ icon: "success", title: "All Records Deleted Successfully", text: `Successfully deleted ${successCount} records.` })
        .then(() => window.location.reload());
    } else {
      Swal.fire({ icon: "warning", title: "Some Records Failed to Delete", text: `Successfully deleted ${successCount} records. ${failureCount} deletions failed.` })
        .then(() => window.location.reload());
    }
  };

  // ---------- linkedin ----------
  const sendLinkedIn = async (comp_id_list) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/linkedin/messages/send`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Authorization': localStorage.getItem('access_token'), 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id_list: comp_id_list })
      });
      if (!response.ok) {
        const errorData = await response.text();
        console.error('API Error:', errorData);
      } else {
        console.log(await response.json());
      }
    } catch (error) {
      console.log('Linkedin message sending error:', error);
    }
  };

  const handleSendLinked = async (company_id) => {
    const rowData = localCompaniesData.find((row) => row.company_id === company_id);

    if (!rowData.content?.trim() || !rowData.content_subject?.trim()) {
      Swal.fire({ icon: 'error', title: 'Empty Messsage', text: 'Please add content before sending the message' });
      return;
    }
    if (!rowData.rep_linkedin_address?.trim()) {
      Swal.fire({ icon: 'error', title: 'Empty Linkedin', text: 'This company have no linkedin reply information' });
      return;
    }

    try {
      Swal.fire({ title: 'Sending...', text: 'Please wait while the linkedin message is being sent.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      sendLinkedIn([company_id]);
    } catch {
      Swal.fire({ icon: 'Error', title: 'Linkedin Sending Error', text: 'There was an error sending the message.' });
    }

    await new Promise((resolve) => setTimeout(resolve, 5000));
    Swal.close();
    await updateData(currentPage, numProspecttoView);
  };

  const handleSendLinkedinSelected = async (all = false) => {
    if (showActionMenu) toggleActionMenu();

    const selectedIds = all
      ? localCompaniesData.map((row) => row.company_id)
      : Object.keys(selectedRows).filter((company_id) => selectedRows[company_id]);

    if (selectedIds.length === 0) return;

    const rowsToSend = selectedIds.filter((company_id) => {
      const row = localCompaniesData.find((row) => row.company_id === company_id);
      return row && ([2, 3, 4, 5, 6, 10].includes(row.linkedin_message_status_id));
    });

    if (rowsToSend.length === 0) {
      Swal.fire({ icon: "error", title: "No Prospect Ready", text: "Please check again the prospect. Linkedin message maybe are not created or sent" });
      return;
    }

    Swal.fire({ title: "Loading...", text: "Please wait while sendind mail.", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try { await sendLinkedIn(rowsToSend); } catch (error) { console.error("Error sending linkedin message", error); }

    Swal.close();
    await updateData(currentPage, numProspecttoView);
    setSelectedRows({}); setSelectAll(false);
  };

  // ---------- autopilot ----------
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedPromptAction, setSelectedPromptAction] = useState(null);
  const [promptData, setPromptData] = useState([]);

  const handleAutoPilotMode = async () => {
    setShowAutoPilotPanel(!showAutoPilotPanel);
    if (!showAutoPilotPanel) {
      try {
        const response = await fetch(`${roboman_api}/nlp/prompts/email-gen`, {
          method: 'GET',
          headers: { 'accept': 'application/json', 'Authorization': localStorage.getItem('access_token') }
        });
        const result = await response.json();
        setPromptData(result || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
  };

  const handleStopAutopilot = async () => {
    const campaignId = localStorage.getItem("campaign_id");
    try {
      const res = await fetch(`${roboman_api}/campaign/${campaignId}/outreach/stop-auto`, {
        method: 'PUT',
        headers: { accept: 'application/json', Authorization: localStorage.getItem('access_token') },
      });
      if (res.ok) {
        setAutoPilotStatus(false);
        Swal.fire({ icon: 'success', title: 'Autopilot Disabled' });
      } else {
        Swal.fire({ icon: 'error', title: 'Failed to stop Autopilot' });
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Network Error' });
    }
  };

  const handleAutoPilot = async () => {
    if (showAutoPilotPanel) setShowAutoPilotPanel(false);
    let prompt_type = "email_generation_v5";
    if (selectedPrompt != null) prompt_type = selectedPrompt.prompt_name;

    try {
      const response = await fetch(`${roboman_api}/campaign/${campaign_id}/outreach/auto`, {
        method: "POST",
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt_name: prompt_type }),
      });
      if (!response.ok) {
        await Swal.fire({ title: "AutoPilot Failed", text: "We encountered an issue while processing AutoPilot. Please try again later.", icon: "error" });
        return;
      }
      await Swal.fire({ title: "AutoPilot Started", text: "AutoPilot is now processing your request. Please refresh the page in a few minutes to check the status.", icon: "success" });
    } catch {
      await Swal.fire({ title: "AutoPilot Failed", text: "We encountered an issue while processing AutoPilot. Please try again later.", icon: "error" });
    }

    updateData(currentPage, numProspecttoView);
    setSelectedRows({}); setSelectAll(false);
    window.location.reload();
  };

  // ---------- menus & misc ----------
  const toggleActionMenu = () => setShowActionMenu(!showActionMenu);
  const toggleConfigMenu = () => setShowConfigMenu(!showConfigMenu);
  const toggleProspectNumberMenu = () => setShowProspectMenu(!showProspectMenu);
  const setNumberForPagination = (number) => { setNumProspecttoView(number); setShowProspectMenu(!showProspectMenu); setCurrentPage(1) };

  const updateEmailStatus = async (companyId, contentSubject, content, emailConfirmationStatusId) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/companies/update-email-status`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': localStorage.getItem("access_token"),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_id: companyId,
          content_subject: contentSubject,
          content: content,
          email_confirmation_status_id: emailConfirmationStatusId
        })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  };

  const updateLinkedinMessage = async (campaignId, companyId, linkedin_message) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/companies/update-linkedin-msg-status`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': localStorage.getItem("access_token"),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ company_id: companyId, linkedin_message })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  };

  const IconWithPopup = ({ message }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="text-lg cursor-pointer ml-2 mb-4">
          <FaQuestionCircle />
        </div>
        {isHovered && (
          <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-[300px] p-2 bg-gray-800 text-white text-sm rounded shadow-lg z-10">
            {message}
          </div>
        )}
      </div>
    );
  };

  const handleUpdateCampaign = () => navigate("/update-campaign");



  const handleProfileClick = (company_id) => {
    const linkedInLink = localCompaniesData.find((row) => row.company_id === company_id)?.rep_linkedin_address;
    localStorage.setItem("LinkedInlink", linkedInLink);
    localStorage.setItem("company_id", company_id);
    navigate("/viewinsight");
  };

  const capitalizeWords = (str) =>
    (str || "")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // --- Representative avatar (image with graceful fallback) ---
  const repAvatarFromRow = (row) => row?.profile_image;

  const Avatar = ({ name, src, size = 32 }) => {
    const [err, setErr] = React.useState(false);
    const initials = (name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => (w[0] || "").toUpperCase())
      .join("");

    if (src && !err) {
      return (
        <img
          src={src}
          alt={name ? `${name}'s profile` : "Profile image"}
          loading="lazy"
          onError={() => setErr(true)}
          className="rounded-full object-cover ring-1 ring-gray-200"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <div
        className="rounded-full bg-gray-200 text-gray-700 grid place-items-center ring-1 ring-gray-200"
        style={{ width: size, height: size }}
        aria-label={name ? `${name} initials` : "Profile placeholder"}
        title={name || "—"}
      >
        <span className="text-xs font-semibold">{initials || "👤"}</span>
      </div>
    );
  };

  const togglePromptPopup = async () => {
    setShowConfigMenu(false);
    setPromptBoxOpen(!isPromptBoxOpen);
    if (!isPromptBoxOpen) {
      try {
        const response = await fetch(`${roboman_api}/nlp/prompts/email-gen`, {
          method: 'GET',
          headers: { 'accept': 'application/json', 'Authorization': localStorage.getItem('access_token') }
        });
        const result = await response.json();
        setPromptData(result || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
  };

  const handlePromptSelect = (prompt) => {
    if (!generatePromptAction) {
      setSelectedPrompt(prompt);
      localStorage.setItem("prompt", promptData.findIndex(p => p === prompt));
    } else {
      setSelectedPromptAction(prompt);
      localStorage.setItem("prompt_action", promptData.findIndex(p => p === prompt));
    }
  };
  const handlePromptCancel = () => { setPromptBoxOpen(false); setGeneratePromptAction(false); };
  const handlePromptConfirm = () => setPromptBoxOpen(false);
  const handleGenerateSelectedPrompt = () => { if (showActionMenu) toggleActionMenu(); togglePromptPopup(); }
  const handleGenerateWithPrompt = async () => { setGeneratePromptAction(true); setPromptBoxOpen(false); await handleGenerateSelected(false); }
  const handleViewSummary = async () => setSelectedCampaign(localStorage.getItem('campaign_id'));
  const handleNextPage = async () => { if (currentPage < totalPages) { setCurrentPage((p) => p + 1); updateData(currentPage + 1, numProspecttoView); } };
  const handlePrevPage = async () => { if (currentPage > 1) { setCurrentPage((p) => p - 1); updateData(currentPage - 1, numProspecttoView); } };
  useEffect(() => () => { localStorage.removeItem("prompt"); localStorage.removeItem("prompt_action"); }, []);

  const handleAnalyzeProfile = async (company_id, rep_id) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/companies/${company_id}/representatives/${rep_id}/humantic/update`, {
        method: 'PUT',
        headers: { 'accept': 'application/json', 'Authorization': localStorage.getItem('access_token') }
      });
      const data = await response.json();
      if (response.ok) {
        Swal.fire({ title: 'Success', text: data.detail, icon: 'success' }).then(() => updateData(currentPage, numProspecttoView));
      } else {
        Swal.fire({ title: 'Error', text: data.detail || 'Status: Please try again later.', icon: 'error' });
      }
    } catch {
      Swal.fire({ title: 'Error', text: 'Status: Please try again later.', icon: 'error' });
    }
  };

  const handleRefreshPage = async () => { await updateData(currentPage, numProspecttoView); };

  const [calendlyLink, setCalendlyLink] = useState("");
  const toggleSetupMeetingPopup = () => { if (showSetupMeetingPopup) setCalendlyLink(""); setShowSetupMeetingPopup(!showSetupMeetingPopup); };

  const handleSetupMeeting = async () => {
    if (calendlyLink && !/^https:\/\/.+/.test(calendlyLink)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Link', text: 'The Calendly link is not in the correct format. Please enter a valid https link.' });
      return;
    }
    try {
      const response = await fetch(`${roboman_api}/campaign/${campaign_id}/update-calendly`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ calendly_link: calendlyLink || null }),
      });
      const data = await response.json();
      if (response.ok) {
        Swal.fire({ icon: 'success', title: 'Success', text: 'Calendly link updated successfully!' });
      } else {
        Swal.fire({ icon: 'error', title: 'Update Failed', text: data?.message || 'Something went wrong while updating the link.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Network Error', text: error.message || 'An unexpected error occurred.' });
    }
  };

  /* ====================== VIEW PROPOSAL (PDF) ====================== */
  const openProposalPdf = (row) => {
    const userId = getUserIdFromToken();
    if (!userId) {
      Swal.fire({ icon: 'error', title: 'Missing user id', text: 'Could not determine your user id from the access token.' });
      return;
    }
    const assets = assetAvailability[row.company_id] || {};
    const candidate = assets.proposalUrl
      || proposalS3UrlPlus(campaign_id, row?.rep_name || '', userId)
      || proposalS3UrlPct(campaign_id, row?.rep_name || '', userId);

    if (assets.proposal === false) {
      Swal.fire({ icon: 'error', title: 'Proposal not found', text: 'This proposal could not be located on S3.' });
      return;
    }
    window.open(candidate, '_blank', 'noopener,noreferrer');
  };

  /* ====================== VOICE NOTE OPEN / MODAL ====================== */
  const openVoiceNote = (row) => {
    const userId = getUserIdFromToken();
    if (!userId) {
      Swal.fire({ icon: 'error', title: 'Missing user id', text: 'Could not determine your user id.' });
      return;
    }
    const assets = assetAvailability[row.company_id] || {};
    const url1 = assets.voiceUrl || voiceS3UrlPlus(campaign_id, row?.rep_name || '', userId);
    const url2 = assets.voiceAltUrl || voiceS3UrlPct(campaign_id, row?.rep_name || '', userId);

    if (assets.voice === false) {
      Swal.fire({ icon: 'error', title: 'Voice note not found', text: 'This voice note could not be located on S3.' });
      return;
    }
    setVoiceModal({ open: true, repName: row?.rep_name || "", url: url1, altUrl: url2, triedAlt: false });
  };
  const closeVoiceModal = () => setVoiceModal(v => ({ ...v, open: false }));
  /* ===================================================================== */

  const handleNextForAutopilotMetadata = async () => {
    navigate('/account?tab=metadata');
  };

  const randomIntent_score = Math.floor(Math.random() * 7);
  // ===== NEW: decide whether to show Intent Score column at all =====
  const showIntentScoreColumn = localCompaniesData.some((row) => {
    const v = row?.intent_score;
    return !isNil(v) && String(v).trim() !== "" && String(v).toLowerCase() !== "null";
  });








  const handleSendSingleEmail = async () => {
    // Only allow if not already sent and not currently sending
    if (!currentEmail || currentEmail?.email_sent || isSendingSingle) return;

    console.log("DATA", currentEmail)

    try {
      setIsSendingSingle(true);

      const res = await fetch(`${roboman_api}/campaign/emails/send/single`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: localStorage.getItem("access_token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_id: currentEmail.company_id,
          draft: currentEmail.draft_type,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to send",
          text: data?.detail || data?.message || "Something went wrong.",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Queued",
        text: data?.message || "Email is set to be sent as requested",
      });

      // ✅ update UI so button becomes disabled immediately
      setEmailData((prev) =>
        prev.map((e, idx) =>
          idx === currentIndex ? { ...e, email_sent: true } : e
        )
      );

      // Optional: refresh company list so status updates (sent/sending etc.)
      await updateData(currentPage, numProspecttoView);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Network error",
        text: err?.message || "Could not call send API.",
      });
    } finally {
      setIsSendingSingle(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <HeaderMainPage />

      {/* Header: new look & responsive */}
      <div className="w-full max-w-[1900px] mx-auto mt-4">
        <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden bg-white">
          <div className="flex flex-col gap-4 p-4 md:p-5 lg:p-6 bg-gradient-to-r from-white via-gray-50 to-white">
            {/* Top row: back + title */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={handleBack}
                  className={`${ICON_GHOST}`}
                  aria-label="Back"
                  title="Back"
                >
                  <IoArrowBack size={18} />
                </button>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-gray-900 truncate">
                  {Campaign_Name}{' '}
                  <span className="whitespace-nowrap"></span>
                </h1>
              </div>

              {/* Action cluster – primary CTA first */}
              <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap justify-end">
                {!autopilotStatus ? (
                  <button
                    ref={promptButtonRef}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-600"
                    onClick={handleNextForAutopilotMetadata}
                    title="Start AutoPilot"
                  >
                    <FiZap className="text-base" />
                    <span className="hidden sm:inline">Start AutoPilot</span>
                  </button>
                ) : (
                  <button
                    onClick={handleStopAutopilot}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-rose-600"
                    title="Stop AutoPilot"
                  >
                    <FiZap className="text-base" />
                    <span className="hidden sm:inline">Stop AutoPilot</span>
                  </button>
                )}

                {/* Secondary tools */}
                <button onClick={handleRefreshPage} className={TOOL_BTN} title="Refresh Page">
                  <FiRefreshCw className="text-base" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                <button onClick={handleViewSummary} className={TOOL_BTN} title="Campaign Summary">
                  <FiEye className="text-base" />
                  <span className="hidden sm:inline">Summary</span>
                </button>

                <button onClick={handleMailboxClick} className={TOOL_BTN} title="Unibox">
                  <FiInbox className="text-base" />
                  <span className="hidden sm:inline">Unibox</span>
                </button>

                {/* Config menu */}
                <div className="relative" ref={ConfigMenuRef}>
                  <button
                    onClick={toggleConfigMenu}
                    className={TOOL_BTN}
                    aria-haspopup="menu"
                    aria-expanded={showConfigMenu}
                    title="Config"
                  >
                    <FiSettings className="text-base" />
                    <span className="hidden sm:inline">Config</span>
                    <span className="sm:hidden">▼</span>
                  </button>
                  {showConfigMenu && (
                    <div role="menu" className={MENU_PANEL}>
                      <button onClick={handleUpdateCampaign} className={MENU_ITEM}>Update Campaign</button>
                      <button onClick={handleAddContactButton} className={MENU_ITEM}>Add Company Manual</button>
                      <button onClick={toggleSetupMeetingPopup} className={`${MENU_ITEM} text-blue-700`}>Setup Meeting</button>
                      <button onClick={handleAddProspectsPopup} className={MENU_ITEM}>Add Prospects</button>
                    </div>
                  )}
                </div>

                {/* Action menu */}
                <div className="relative" ref={ActionMenuRef}>
                  <button
                    onClick={toggleActionMenu}
                    className={TOOL_BTN}
                    aria-haspopup="menu"
                    aria-expanded={showActionMenu}
                    title="Actions"
                  >
                    <span className="text-base">⋯</span>
                    <span className="hidden sm:inline">Actions</span>
                  </button>
                  {showActionMenu && (
                    <div role="menu" className={MENU_PANEL}>
                      <button onClick={() => handleGenerateSelected(false)} className={MENU_ITEM}>Generate</button>
                      <button onClick={handleGenerateSelectedPrompt} className={MENU_ITEM}>Generate with prompt</button>
                      <button onClick={() => handleSendSelected(false)} className={MENU_ITEM}>Send Email</button>
                      <button onClick={() => handleSendLinkedinSelected(false)} className={MENU_ITEM}>Send Linkedin</button>
                      <button onClick={handleDeleteSelected} className={`${MENU_ITEM} text-red-700`}>Delete</button>
                    </div>
                  )}
                </div>

                {/* View menu (page size) */}
                <div className="relative" ref={ProspectViewMenuRef}>
                  <button
                    onClick={toggleProspectNumberMenu}
                    className={TOOL_BTN}
                    aria-haspopup="menu"
                    aria-expanded={showProspectMenu}
                    title="Rows per page"
                  >
                    <span className="text-base">▦</span>
                    <span className="hidden sm:inline">View</span>
                  </button>
                  {showProspectMenu && (
                    <div role="menu" className={MENU_PANEL}>
                      {[10, 20, 50, 100].map(n => (
                        <button key={n} onClick={() => setNumberForPagination(n)} className={MENU_ITEM}>{n}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Table / body */}
          <div className="overflow-x-auto">
            <div className="h-[calc(100vh-270px)] overflow-y-auto pb-16">
              <table className="w-full">
                <thead className="bg-gradient-to-b from-gray-50 to-white sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                        aria-label="Select all"
                      />
                    </th>
                    <th className="w-32 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Company</th>
                    <th className="w-32 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Industry</th>
                    <th className="w-32 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Representative</th>
                    {showIntentScoreColumn && (
                      <th className="w-32 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Intent Score
                      </th>
                    ) }
                    <th className="w-64 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Progress</th>
                    <th className="w-48 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Email Status</th>
                    <th className="w-48 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Linkedin Status</th>
                    <th className="w-48 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-100">
                  {localCompaniesData.map(row => {
                    const contentText = row?.content
                      ? new DOMParser().parseFromString(row.content, "text/html").body.textContent
                      : "";

                    const emailStep = isNil(row?.outreach_progress) ? null : clampStep(row?.outreach_progress);
                    const liStep = isNil(row?.linkedin_outreach_progress) ? null : clampStep(row?.linkedin_outreach_progress);

                    const EmailIcon = iconFor(EMAIL_STEPS, emailStep);
                    const LiIcon = iconFor(LI_STEPS, liStep);

                    // Availability (true|false|null) for this row
                    const assets = assetAvailability[row.company_id] || {};

                    // NEW: disable proposal/voice when email status is "Not Started"
                    const emailNotStarted =
                      (typeof row.email_confirmation_status_id === 'number' &&
                        row.email_confirmation_status_id === 1) ||
                      (typeof row.email_confirmation_status_id === 'string' &&
                        row.email_confirmation_status_id.toLowerCase() === 'not started');

                    const proposalDisabled = emailNotStarted || assets.proposal === false; // disabled if Not Started or missing on S3
                    const voiceDisabled = emailNotStarted || assets.voice === false;       // disabled if Not Started or missing on S3

                    return (
                      <tr key={row.company_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows[row.company_id] || false}
                            onChange={() => handleRowSelect(row.company_id)}
                            className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            aria-label={`Select ${row.company_name}`}
                          />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[290px] min-w-[290px]" title={row.company_name}>
                            {capitalizeWords(row.company_name)}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600 truncate max-w-[260px] min-w-[260px]" title={row.industry || ''}>
                            {capitalizeWords(row.industry)}
                          </div>
                        </td>

                        {/* Representative with profile image */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3 max-w-[230px] min-w-[230px]">
                            <Avatar name={row?.rep_name} src={repAvatarFromRow(row)} size={32} />
                            <div className="text-sm text-gray-700 truncate" title={row?.rep_name || ""}>
                              {capitalizeWords(row?.rep_name) || "—"}
                            </div>
                          </div>
                        </td>

                        {showIntentScoreColumn && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className="text-sm font-medium text-gray-900 max-w-[10px] min-w-[10px]"
                              title={row.intent_score ?? ''}
                            >
                              {row.intent_score}
                            </div>
                          </td>
                        )}

                        {/* Progress */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openProgress(row)}
                            className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1.5 shadow-sm hover:bg-gray-50"
                            title="View full progress"
                          >
                            <Chip
                              icon={EmailIcon}
                              text={`Email: ${labelFor(EMAIL_STEPS, row?.outreach_progress)}`}
                              active={!isNil(row?.outreach_progress)}
                            />
                            <Chip
                              icon={LiIcon}
                              text={`LinkedIn: ${labelFor(LI_STEPS, row?.linkedin_outreach_progress)}`}
                              active={!isNil(row?.linkedin_outreach_progress)}
                            />
                          </button>
                        </td>

                        {/* Email Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex items-center gap-2 text-xs leading-5 font-medium rounded-full ${typeof row.email_confirmation_status_id === 'number'
                              ? getStatusStyle(row.email_confirmation_status_id).style
                              : ''
                              }`}
                            title={
                              typeof row.email_confirmation_status_id === 'string'
                                ? row.email_confirmation_status_id
                                : getStatusStyle(row.email_confirmation_status_id).text
                            }
                          >
                            <span className="inline-block w-2 h-2 rounded-full bg-current" />
                            {typeof row.email_confirmation_status_id === 'string'
                              ? row.email_confirmation_status_id
                              : getStatusStyle(row.email_confirmation_status_id).text}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex items-center gap-2 text-xs leading-5 font-medium rounded-full ${typeof row.linkedin_message_status_id === 'number'
                              ? getLinkedinStyleStatus(row.linkedin_message_status_id).style
                              : ''
                              }`}
                            title={
                              typeof row.linkedin_message_status_id === 'string'
                                ? row.linkedin_message_status_id
                                : getLinkedinStyleStatus(row.linkedin_message_status_id).text
                            }
                          >
                            <span className="inline-block w-2 h-2 rounded-full bg-current" />
                            {typeof row.linkedin_message_status_id === 'string'
                              ? row.linkedin_message_status_id
                              : getLinkedinStyleStatus(row.linkedin_message_status_id).text}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleReview(row.company_id)}
                              className={`${BTN_SMALL} ${[].includes(row.email_confirmation_status_id)
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-600'
                                }`}
                              disabled={[].includes(row.email_confirmation_status_id)}
                              title="Review email"
                            >
                              Review
                            </button>

                            {/* View proposal PDF from S3 */}
                            <button
                              onClick={() => openProposalPdf(row)}
                              disabled={proposalDisabled}
                              className={`${BTN_SMALL} ${proposalDisabled
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-slate-700 text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-700'
                                }`}
                              title={proposalDisabled ? 'Proposal not available' : 'View Proposal (PDF)'}
                            >
                              <FiFileText className="text-base" />
                            </button>

                            {/* Play Voice Note */}
                            <button
                              onClick={() => openVoiceNote(row)}
                              disabled={voiceDisabled}
                              className={`${BTN_SMALL} ${voiceDisabled
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-600'
                                }`}
                              title={voiceDisabled ? 'Voice note not available' : 'Play Voice Note'}
                            >
                              <FiMic className="text-base" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between gap-3 w-full p-3 md:p-4 border-t border-gray-200 bg-white sticky bottom-0">
              <button onClick={handlePrevPage} disabled={currentPage === 1} className={`${BTN_NEUTRAL} disabled:opacity-50`}>
                Prev
              </button>
              <div className="text-sm text-gray-700">
                Page <span className="font-semibold text-gray-900">{currentPage}</span> of {totalPages}
              </div>
              <button onClick={handleNextPage} disabled={currentPage === totalPages} className={`${BTN_NEUTRAL} disabled:opacity-50`}>
                Next
              </button>
            </div>
          </div>

          {/* DISC & OCEAN modals (unchanged) */}
          <ExplainDISC isOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} />
          <ExplainOCEAN isOpen={isOCEANModalOpen} closeModal={() => setIsOCEANModalOpen(false)} />
        </div>
      </div>

      {/* PromptBox (unchanged) */}
      <PromptBox
        isVisible={showPromptBox}
        promptText={promptText}
        setPromptText={setPromptText}
        handleAutoSend={handleAutoSend}
        position={promptButtonPosition}
        promptBoxRef={promptBoxRef}
      />

      {/* Review Modal — "Preview / Edit" toggle */}
      {showPopup && currentEmail && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl md:w-[980px] md:max-h-[85vh] overflow-y-auto border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xl font-bold text-gray-900">{getTitle()}</h3>
              <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setPreviewMode(true)}
                  className={`px-3 py-1.5 text-sm font-medium ${previewMode ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Preview
                </button>
                <button
                  onClick={() => setPreviewMode(false)}
                  className={`px-3 py-1.5 text-sm font-medium ${!previewMode ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Edit
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <button onClick={handlePrev} className={`${BTN_NEUTRAL} px-3 py-1 flex items-center gap-1`}>
                <FaArrowLeft size={16} /> Prev
              </button>
              <span className="text-sm font-medium text-gray-700">
                {emailData.length ? currentIndex + 1 : 0} / {emailData.length}
              </span>
              <button onClick={handleNext} className={`${BTN_NEUTRAL} px-3 py-1 flex items-center gap-1`}>
                Next <FaArrowRight size={16} />
              </button>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={currentEmail.subject}
                onChange={(e) => handleChangeSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="mt-4">
              {previewMode ? (
                <EmailTemplatePreview subject={currentEmail.subject} htmlBody={currentEmail.body} calendlyLink={calendlyLink || null} />
              ) : (
                <ReactQuill
                  key={currentIndex}
                  theme="snow"
                  value={currentEmail.body}
                  onChange={handleChangeBody}
                  modules={modules}
                  className="md:h-[420px] h-[250px] rounded-lg"
                />
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleSendSingleEmail}
                className={`${BTN_SUCCESS} ${
                  currentEmail?.email_sent || isSendingSingle ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={!!currentEmail?.email_sent || isSendingSingle}
              >
                {isSendingSingle ? "Sending..." : currentEmail?.email_sent ? "Already Sent" : "Approve and Send"}
              </button>
              <button onClick={handleUpdateMail} className={BTN_PRIMARY}>
                Update
              </button>
              <button onClick={handleCancel} className={BTN_NEUTRAL}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Prospect Modal */}
      {isPopUpAddContact && (
        <div className={`fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm ${MODAL_Z}`}>
          <div className="w-[700px] bg-white p-6 rounded-2xl shadow-2xl border border-gray-200">
            <div className="flex items-center">
              <h2 className="text-xl font-bold mb-4 text-gray-900">Add New Prospect</h2>
              <IconWithPopup message={"Please provide at least the Company Name, Industry, Company Email and Reply Email for sending email"} />
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={newContact.company_name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company name"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                      Industry <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="industry"
                      value={newContact.industry}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your industry"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                      Company Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="company_email"
                      value={newContact.company_email}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company email"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">Company LinkedIn</label>
                    <input
                      type="text"
                      name="company_linkedin"
                      value={newContact.company_linkedin}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company LinkedIn"
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                      Representative Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="rep_name"
                      value={newContact.rep_name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company Reply Name"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                      Representative Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="rep_email"
                      value={newContact.rep_email}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company Reply Email"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">Representative LinkedIn</label>
                    <input
                      type="text"
                      name="rep_linked"
                      value={newContact.rep_linked}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Your company Reply LinkedIn"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={handlePopUpClose} className={BTN_NEUTRAL}>
                  Cancel
                </button>
                <button type="submit" className={BTN_PRIMARY} onClick={handleAddContact}>
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Prompt Selection Modal */}
      {isPromptBoxOpen && (
        <div className={`fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm ${MODAL_Z}`}>
          <div className="bg-white p-6 rounded-2xl w-full max-w-3xl shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-bold text-gray-900">Prompt Box Selection</h1>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={togglePromptPopup}
                aria-label="Close"
              >
                ✖
              </button>
            </div>

            <p className="mb-3 text-gray-700">Please select one of the prompts to generate an email:</p>

            {promptData.length > 0 ? (
              promptData.map((prompt, index) => (
                <div
                  key={prompt.prompt_id}
                  className={`border-2 p-4 mb-3 rounded-xl cursor-pointer transition ${generatePromptAction
                    ? selectedPromptAction === prompt
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-gray-300'
                    : selectedPrompt === prompt
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => handlePromptSelect(prompt)}
                >
                  <h2 className="text-base font-semibold text-gray-900">
                    Email Prompt Generation {index + 1}
                  </h2>
                  <textarea
                    className="w-full mt-2 p-2 border rounded-lg h-40 bg-gray-50"
                    rows="10"
                    value={prompt.prompt_content ? prompt.prompt_content.replace(/<br\s*\/?>/g, "\n") : ""}
                    readOnly
                  ></textarea>
                </div>
              ))
            ) : (
              <p className="text-gray-500">Loading prompts...</p>
            )}

            <div className="flex justify-between items-center mt-4">
              <p className="text-gray-700">
                {generatePromptAction
                  ? localStorage.getItem("prompt_action") !== null
                    ? `You have selected Prompt ${parseInt(localStorage.getItem("prompt_action"), 10) + 1}`
                    : "You have not selected any Prompt"
                  : localStorage.getItem("prompt") !== null
                    ? `You have selected Prompt ${parseInt(localStorage.getItem("prompt"), 10) + 1}`
                    : "You have not selected any Prompt"}
              </p>
              <div className="flex gap-2">
                <button className={BTN_NEUTRAL} onClick={handlePromptCancel}>
                  Cancel
                </button>
                <button
                  className={BTN_PRIMARY}
                  onClick={generatePromptAction ? handleGenerateWithPrompt : handlePromptConfirm}
                  disabled={!selectedPrompt && !selectedPromptAction}
                >
                  {generatePromptAction ? "Start Generate" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Summary wrapper */}
      {selectedCampaign && (
        <div className={`relative ${MODAL_Z}`}>
          <CampaignSummary
            campaignId={selectedCampaign}
            onClose={() => setSelectedCampaign(null)}
          />
        </div>
      )}

      {/* Autopilot Panel */}
      {showAutoPilotPanel && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white w-[1200px] h-[800px] p-8 rounded-2xl shadow-2xl border border-gray-200 relative overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Autopilot</h2>
              <button
                onClick={() => setShowAutoPilotPanel(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✖
              </button>
            </div>

            <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <h2 className="text-gray-800 text-lg font-bold">
                PLEASE TAKE CAUTIOUS BEFORE DOING AUTOPILOT
              </h2>
              <p className="text-gray-700 text-sm mt-1">
                When <strong>AUTOPILOT</strong> is running, <strong>EMAILS</strong> and{" "}
                <strong>LINKEDIN MESSAGE</strong> will be created and sent automatically to the
                prospect. Please select a prompt before starting <strong>AUTOPILOT</strong>.
              </p>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-4 h-[500px] overflow-auto">
              {promptData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {promptData.map((prompt, index) => (
                    <div
                      key={prompt.prompt_id}
                      className={`border-2 p-4 rounded-xl cursor-pointer transition ${generatePromptAction
                        ? selectedPromptAction === prompt
                          ? 'border-blue-500 ring-2 ring-blue-100'
                          : 'border-gray-200 hover:border-gray-300'
                        : selectedPrompt === prompt
                          ? 'border-blue-500 ring-2 ring-blue-100'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => handlePromptSelect(prompt)}
                    >
                      <h2 className="text-base font-semibold text-gray-900">
                        Email Prompt Generation {index + 1}
                      </h2>
                      <textarea
                        className="w-full mt-2 p-2 border rounded-lg h-72 bg-gray-50"
                        value={prompt.prompt_content ? prompt.prompt_content.replace(/<br\s*\/?>/g, "\n") : ""}
                        readOnly
                      ></textarea>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Loading prompts...</p>
              )}

              <div className="flex flex-col items-start mt-4 gap-1">
                <p className="text-gray-700">
                  {generatePromptAction
                    ? localStorage.getItem("prompt_action") !== null
                      ? `You have selected Prompt ${parseInt(localStorage.getItem("prompt_action"), 10) + 1}`
                      : "You have not selected any Prompt"
                    : localStorage.getItem("prompt") !== null
                      ? `You have selected Prompt ${parseInt(localStorage.getItem("prompt"), 10) + 1}`
                      : "You have not selected any Prompt"}
                </p>
                <p className="text-gray-700">
                  <strong>NOTE:</strong> If you do not select a prompt, the default will be{" "}
                  <strong>Prompt 1</strong>.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 items-center mt-4">
              <div className="flex justify-center">
                <button onClick={handleAutoPilot} className={BTN_PRIMARY + " w-[300px]"}>
                  Start Autopilot
                </button>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowAutoPilotPanel(false)}
                  className={BTN_DANGER + " w-[300px]"}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Setup Meeting Modal */}
      {showSetupMeetingPopup && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white w-[500px] p-6 rounded-2xl shadow-2xl border border-gray-200">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Set Up Meeting Link</h2>
            <h3 className="text-gray-700 mb-4">
              Please create your meeting link in Calendly and put your link here to be added in mail
              generation.
            </h3>
            <div className="relative w-full mb-4">
              <textarea
                value={calendlyLink}
                onChange={(e) => setCalendlyLink(e.target.value)}
                placeholder="Enter your Calendly link (optional)"
                className="w-full h-[100px] px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={toggleSetupMeetingPopup} className={BTN_NEUTRAL}>
                Cancel
              </button>
              <button onClick={handleSetupMeeting} className={BTN_PRIMARY}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Trail Modal */}
      {progressModal.open && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white w-full max-w-3xl p-6 rounded-2xl shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold text-gray-900">
                Progress — {progressModal.companyName}
              </h2>
              <button
                onClick={closeProgress}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✖
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-4">
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FiMail className="text-base" /> Email Sequence
                </div>
                <Trail steps={EMAIL_STEPS} current={progressModal.emailStep} />
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FaLinkedin className="text-base" /> LinkedIn Sequence
                </div>
                <Trail steps={LI_STEPS} current={progressModal.liStep} />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={closeProgress} className={BTN_NEUTRAL}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Note Player Modal */}
      {voiceModal.open && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Voice note — {capitalizeWords(voiceModal.repName || '')}
              </h2>
              <button
                onClick={closeVoiceModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✖
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
              <audio
                controls
                preload="none"
                src={voiceModal.url}
                className="w-full"
                onError={() => {
                  if (!voiceModal.triedAlt && voiceModal.altUrl) {
                    setVoiceModal((v) => ({ ...v, url: v.altUrl, triedAlt: true }));
                  } else {
                    Swal.fire({
                      icon: 'error',
                      title: 'Voice note not found',
                      text: 'We could not load this voice note from S3.',
                    });
                  }
                }}
              >
                Your browser does not support the audio element.
              </audio>
            </div>

            <div className="mt-4 flex justify-end">
              <button onClick={closeVoiceModal} className={BTN_NEUTRAL}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Prospects Modal */}
      {showAddProspectsPopup && (
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center ${MODAL_Z}`}>
          <div className="bg-white w-[500px] p-6 rounded-2xl shadow-2xl border border-gray-200">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Add Additional Prospects</h2>
            <p className="text-gray-700 mb-4">
              Select how many new prospects you want to fetch for this campaign.
            </p>

            {/* Slider input */}
            <div className="mb-6">
              <input
                type="range"
                min="1"
                max="450"
                step="1"
                value={prospectsToAdd}
                onChange={(e) => setProspectsToAdd(Number(e.target.value))}
                className="w-full accent-indigo-600"
              />
              <div className="text-center mt-2 text-gray-800 font-semibold">
                {prospectsToAdd} Prospects
              </div>
            </div>

            {/* Manual input */}
            <div className="mb-6">
              <label className="block text-gray-700 mb-1 font-medium">Enter manually</label>
              <input
                type="number"
                value={prospectsToAdd}
                onChange={(e) => setProspectsToAdd(Number(e.target.value))}
                min="1"
                className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                placeholder="e.g. 50"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={handleCloseAddProspects} className={BTN_NEUTRAL}>
                Cancel
              </button>
              <button onClick={handleAddProspects} className={BTN_PRIMARY}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default EmailStatus;
